import React, {useState} from 'react';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import {VideoEditorText} from '../VideoEditorText';
import {appText} from '../../../assets/text/appText';
import Button from '../../../uiToolkit/Buttons/Button';
import {useEditMediaState} from './useEditMediaState';
import EditMediaSlotsPanel from './EditMediaSlotsPanel';
import EditMediaBrowserPanel from './EditMediaBrowserPanel';
import {
  SaveArtifact,
  Slot,
  isSlotLocked,
  slotDurationOf,
} from './editMediaTypes';
import styles from './EditMediaModal.module.css';

interface Props {
  shown: boolean;
  chunk: GeneratedVideoChunk | null;
  organizationId: string;
  onSave: (chunkId: string, artifacts: SaveArtifact[]) => Promise<void> | void;
  onReplaceAll: (
    chunkId: string,
    source: 'library' | 'storyblocks',
    libraryMediaFileId: string | null,
    storyBlocksVideoId: number | null,
    kind: 'image' | 'video',
  ) => Promise<void> | void;
  onCancel: () => void;
  isLoading?: boolean;
}

const EditMediaModal = React.memo(
  ({
    shown,
    chunk,
    organizationId,
    onSave,
    onReplaceAll,
    onCancel,
    isLoading,
  }: Props) => {
    const state = useEditMediaState({shown, chunk});
    const [isSaving, setIsSaving] = useState(false);

    if (!shown || !chunk) return null;

    const handleSave = async () => {
      if (isSaving) return;
      setIsSaving(true);
      try {
        if (state.isReplaceAllMode) {
          if (!state.pending) return;
          await onReplaceAll(
            chunk.id,
            state.pending.source,
            state.pending.libraryMediaFileId,
            state.pending.storyBlocksVideoId,
            state.pending.kind,
          );
        } else {
          if (!state.hasChanges) return;
          // Document chunks (a locked full-length slide as base layer):
          // UPDATE_ARTIFACTS re-renders with the generation's
          // SlideToArtifactRatio, so on source-only documents the slide
          // covers the new media and the edit looks like a no-op. When the
          // edit boils down to a single new clip, the full-replace endpoints
          // (which do override the ratio) produce a visible result.
          const chunkDuration = state.chunkDuration;
          const hasBaseSlide = state.slots.some(
            (s: Slot) =>
              chunkDuration > 0 &&
              isSlotLocked(s) &&
              slotDurationOf(s) >= chunkDuration,
          );
          const editableWithMedia = state.slots.filter(
            (s: Slot) => !isSlotLocked(s) && s.current,
          );
          const soleSlot =
            editableWithMedia.length === 1 ? editableWithMedia[0] : null;
          const soleMediaChanged =
            !!soleSlot &&
            (soleSlot.original === null ||
              soleSlot.current!.libraryMediaFileId !==
                soleSlot.original.libraryMediaFileId ||
              soleSlot.current!.storyBlocksVideoId !==
                soleSlot.original.storyBlocksVideoId);
          if (
            hasBaseSlide &&
            soleSlot &&
            soleMediaChanged &&
            (soleSlot.current!.libraryMediaFileId ||
              soleSlot.current!.storyBlocksVideoId)
          ) {
            await onReplaceAll(
              chunk.id,
              soleSlot.current!.source,
              soleSlot.current!.libraryMediaFileId,
              soleSlot.current!.storyBlocksVideoId,
              soleSlot.current!.kind,
            );
          } else {
            await onSave(chunk.id, state.buildPayload());
          }
        }
      } finally {
        setIsSaving(false);
      }
    };

    const busy = isLoading || isSaving;
    const saveLabel = state.isReplaceAllMode
      ? VideoEditorText.editMediaReplaceAllSave
      : VideoEditorText.editMediaSaveAll;
    const saveDisabled = state.isReplaceAllMode
      ? !state.pending || busy
      : !state.hasChanges || !state.isDurationValid || busy;

    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2 className={styles.title}>
              {VideoEditorText.editMediaModalTitle}
            </h2>
            <button
              className={styles.closeButton}
              onClick={onCancel}
              disabled={busy}
              aria-label={appText.cancel}>
              <span className="icon-x-close" />
            </button>
          </div>

          <div className={styles.body}>
            <EditMediaSlotsPanel state={state} disabled={busy} />
            <EditMediaBrowserPanel
              state={state}
              organizationId={organizationId}
              chunkId={chunk.id}
              disabled={busy}
            />
          </div>

          <div className={styles.footer}>
            <Button
              title={appText.cancel}
              onClick={onCancel}
              uiType="text"
              disabled={busy}
            />
            {!state.isReplaceAllMode && (
              <Button
                title={VideoEditorText.editMediaApply}
                onClick={state.applyPending}
                uiType="secondary"
                disabled={!state.canApply || busy}
              />
            )}
            <Button
              title={saveLabel}
              onClick={handleSave}
              uiType="action"
              disabled={saveDisabled}
              isLoading={busy}
            />
          </div>
        </div>
      </div>
    );
  },
);

export default EditMediaModal;
